import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

/**
 * A dropdown we draw, with a box to search it.
 *
 * `SelectField` beside this one is the same idea without the search, and says
 * why either exists: a native `<select>` is painted by the operating system, so
 * its list, row height and highlight colour ignore the design and look
 * different on every machine. This adds the one thing that list could not do —
 * find a row by typing part of it — for pickers that are only short on a test
 * account. A homeowner with three live jobs does not need it; a contractor
 * running thirty scrolls a list where every row starts "Phoenix — ".
 *
 * ── The ARIA shape, and why focus stays put ────────────────────────────────
 * The search box is the combobox; the rows are a listbox it owns. Focus never
 * leaves the input — the active row is pointed at with `aria-activedescendant`
 * instead — which is what lets someone type and arrow at the same time without
 * the keystrokes going somewhere else. Rows are `role="option"` divs rather
 * than buttons for the same reason: a focusable row would compete for focus
 * with the thing doing the filtering, and `mousedown` is prevented so clicking
 * one cannot blur the input out from under the click.
 *
 * Escape hands focus back to the trigger rather than dropping it at the top of
 * the document, which is the difference between closing a menu and losing your
 * place on the page.
 */

export interface SearchableOption {
  value: string;
  label: string;
  /** Optional second line, searched along with the label. */
  hint?: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  placeholder?: string;
  /** Shown inside the search box. */
  searchPlaceholder?: string;
  /** What the search box is called, for screen readers. */
  searchLabel?: string;
  /** Shown in place of the rows when nothing matches, or there is nothing. */
  emptyMessage?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  /**
   * Id of the visible label naming this control.
   *
   * Needed because `<label for>` pointing at the trigger does the WRONG thing:
   * a button is a labelable element, so the label wins the accessible name
   * outright and the button's own text — the current choice — is dropped. The
   * control then announces "Project type" and never says what is selected.
   * Passing the label's id here names it "<label> <current choice>" instead,
   * which is the combobox pattern and the only way both are heard.
   */
  labelledBy?: string;
  /** Points the trigger at its error text, the way a plain input would. */
  describedBy?: string;
  className?: string;
}

/** Escapes a query before it goes anywhere near a RegExp. */
const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * The matched run of characters, bolded.
 *
 * Without it a filtered list is a wall of near-identical rows and the reader
 * has to find their own text in each one — which is the job they typed it to
 * avoid.
 */
const Highlight: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const trimmed = query.trim();
  if (!trimmed) return <>{text}</>;

  const parts = text.split(new RegExp(`(${escapeRegExp(trimmed)})`, 'ig'));
  return (
    <>
      {parts.map((part, index) =>
        part.toLowerCase() === trimmed.toLowerCase() ? (
          <mark key={index} className="bg-transparent font-extrabold text-inherit">
            {part}
          </mark>
        ) : (
          <React.Fragment key={index}>{part}</React.Fragment>
        )
      )}
    </>
  );
};

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Choose one',
  searchPlaceholder = 'Search...',
  searchLabel = 'Search options',
  emptyMessage = 'Nothing to choose from',
  disabled = false,
  invalid = false,
  id,
  labelledBy,
  describedBy,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const reactId = useId();
  const listId = `${reactId}-listbox`;
  /** The trigger must be able to name itself, so it always has an id. */
  const triggerId = id ?? `${reactId}-trigger`;
  const optionId = (index: number) => `${reactId}-option-${index}`;

  const selected = options.find((option) => option.value === value) || null;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        (option.hint || '').toLowerCase().includes(needle)
    );
  }, [options, query]);

  /** Opening lands on the current choice, not at the top of a scrolled list. */
  const openMenu = () => {
    if (disabled) return;
    setQuery('');
    const at = options.findIndex((option) => option.value === value);
    setActiveIndex(at >= 0 ? at : 0);
    setOpen(true);
  };

  const closeMenu = (returnFocus = true) => {
    setOpen(false);
    setQuery('');
    if (returnFocus) triggerRef.current?.focus();
  };

  const choose = (option: SearchableOption) => {
    onChange(option.value);
    closeMenu();
  };

  // Focus the search box as the panel appears, so typing works immediately.
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Filtering shrinks the list under the cursor; without this the active row
  // can end up past the end of it and Enter selects nothing.
  useEffect(() => {
    setActiveIndex((current) => (current >= filtered.length ? 0 : current));
  }, [filtered.length]);

  // Keep the active row on screen while arrowing.
  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    // Optional call, not defensiveness for its own sake: scrollIntoView is
    // missing in jsdom and in some embedded webviews, and keeping a row in view
    // is a nicety that must never be able to throw inside a keydown handler and
    // take the whole control down with it.
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, open]);

  // Clicking away closes it. A menu drawn in the page, unlike one drawn by the
  // OS, is one a reader can otherwise get stuck inside.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (filtered.length) setActiveIndex((current) => (current + 1) % filtered.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (filtered.length) {
          setActiveIndex((current) => (current - 1 + filtered.length) % filtered.length);
        }
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(Math.max(filtered.length - 1, 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (filtered[activeIndex]) choose(filtered[activeIndex]);
        break;
      case 'Escape':
        event.preventDefault();
        closeMenu();
        break;
      case 'Tab':
        // Leaving by keyboard closes it rather than stranding an open panel.
        closeMenu(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={labelledBy ? `${labelledBy} ${triggerId}` : undefined}
        /* No aria-invalid: the implicit role here is button, which does not
           support it, so it was announced to nobody. The error text is still
           reached through aria-describedby, and the invalid state is still
           shown visually below. */
        aria-describedby={describedBy}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openMenu();
          }
        }}
        className={`flex h-[46px] w-full items-center justify-between gap-3 rounded-brand border bg-white px-4 text-left text-[15px] transition-all duration-200 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:opacity-75 ${
          invalid
            ? 'border-error focus:border-error focus:ring-error/30'
            : open
              ? 'border-orange-brand ring-2 ring-orange-brand/30'
              : 'border-neutral-300 hover:border-neutral-400 focus:border-orange-brand focus:ring-orange-brand/30'
        }`}
      >
        <span className={`truncate ${selected ? 'text-neutral-800' : 'text-neutral-500'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 flex-none text-orange-brand transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div
          className="absolute left-0 z-40 mt-2 w-full overflow-hidden rounded-[12px] bg-white"
          style={{ border: '1px solid #e4e1db', boxShadow: '0 16px 40px rgba(27,42,74,0.15)' }}
        >
          <div className="flex items-center gap-2 border-b px-3" style={{ borderColor: '#e4e1db' }}>
            <Search className="h-4 w-4 flex-none text-neutral-400" aria-hidden="true" />
            <input
              ref={searchRef}
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={filtered.length ? optionId(activeIndex) : undefined}
              aria-label={searchLabel}
              placeholder={searchPlaceholder}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onSearchKeyDown}
              /**
               * No focus ring on this one, deliberately.
               *
               * The app rings every focused element globally
               * (`*:focus-visible` in index.css), which is right everywhere
               * else and wrong here: it drew a second rectangle a few pixels
               * inside a panel that already has its own border, so the search
               * row read as a box inside a box.
               *
               * Focus is still evident without it, which is the only reason
               * this is allowed to go: the panel exists ONLY while this input
               * holds focus, and a text field shows a caret. `!` because the
               * global rule is a catch-all with higher specificity than a
               * utility class, so nothing quieter than important beats it.
               */
              className="h-[42px] w-full bg-transparent text-[14px] text-neutral-800 outline-none placeholder:text-neutral-500 focus:!ring-0 focus-visible:!ring-0 focus-visible:!ring-offset-0"
            />
          </div>

          <div ref={listRef} id={listId} role="listbox" className="max-h-[14rem] overflow-y-auto p-1.5">
            {filtered.map((option, index) => {
              const isSelected = option.value === value;
              const isActive = index === activeIndex;
              return (
                <div
                  key={option.value}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  data-index={index}
                  // Stops the click stealing focus from the search box, which
                  // would close the panel before the click lands.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(option)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`flex min-h-[44px] cursor-pointer items-center justify-between gap-2 rounded-[8px] px-2.5 py-2 text-[14px] transition-colors ${
                    isSelected
                      ? 'bg-orange-brand text-white'
                      : isActive
                        ? 'bg-cream text-navy'
                        : 'text-navy'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-semibold">
                      <Highlight text={option.label} query={query} />
                    </span>
                    {option.hint && (
                      <span
                        className={`mt-0.5 block truncate text-[12px] ${
                          isSelected ? 'text-white/80' : 'text-text-mid'
                        }`}
                      >
                        <Highlight text={option.hint} query={query} />
                      </span>
                    )}
                  </span>
                  {isSelected && <Check className="h-4 w-4 flex-none" aria-hidden="true" />}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <p className="px-2.5 py-6 text-center text-[13px] text-text-mid">
                {query.trim() ? `No matches for “${query.trim()}”` : emptyMessage}
              </p>
            )}
          </div>

          {/* Announced as the list shrinks, so a screen reader hears the result
              of typing rather than only seeing it. */}
          <p aria-live="polite" className="sr-only">
            {filtered.length} {filtered.length === 1 ? 'option' : 'options'} available
          </p>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
